import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/authContext';
import useAuthApi from '../../hooks/useAuthApi';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

export default function SwitchIdentity() {
  const authCtx = useAuth();
  const navigate = useNavigate();
  const { switchIdentity, searchSwitchIdentityUsers } = useAuthApi();

  const [options, setOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const deviceTypeRef = useRef(null);
  // Pflichtangabe des Backends (10-500 Zeichen). Steht im Audit-Log und geht per
  // Mail an die Person, deren Kennung übernommen wird.
  const reasonRef = useRef(null);
  const lastSearchRef = useRef(null);

  const onSwitch = useCallback(async () => {
    const userId = selectedUser?.userId ? String(selectedUser.userId) : '';
    if (!userId) {
      toast.error('Bitte einen Nutzer auswählen');
      return;
    }

    if (selectedUser?.isActive === false) {
      toast.error('Der ausgewählte Nutzer ist inaktiv');
      return;
    }

    const reason = String(reasonRef.current?.value || '').trim();
    if (reason.length < 10) {
      toast.error('Bitte einen Grund mit mindestens 10 Zeichen angeben');
      return;
    }

    try {
      const deviceType = String(deviceTypeRef.current?.value || '').trim();
      const result = await switchIdentity(userId, deviceType, reason);
      if (!result?.token) {
        toast.error('Fehler beim Wechseln der Identität');
        return;
      }

      await authCtx.login(result.token, result);
      toast.success('Identität geändert');
      navigate('/welcome');
    } catch (e) {
      if (e?.code === 'USER_INACTIVE') {
        toast.error('Der ausgewählte Nutzer ist inaktiv');
        return;
      }
      if (e?.code === 'JWT_EXPIRED') {
        toast.error('Sitzung abgelaufen. Bitte erneut anmelden');
        return;
      }
      if (e?.code === 'JWT_INVALID') {
        toast.error('Sitzung ungültig. Bitte erneut anmelden');
        return;
      }
      toast.error('Fehler beim Wechseln der Identität');
    }
  }, [authCtx, navigate, selectedUser, switchIdentity]);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const search = inputValue.trim();
    debounceRef.current = setTimeout(async () => {
      if (lastSearchRef.current === search) {
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        lastSearchRef.current = search;
        const users = await searchSwitchIdentityUsers(search, 20);
        setOptions(users);
      } catch (e) {
        toast.error('Fehler beim Laden der Nutzer');
      } finally {
        setIsLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, searchSwitchIdentityUsers]);

  const optionLabel = useCallback(option => {
    const name = option?.userName || 'Unbekannt';
    const extra = option?.email || option?.username || option?.userId || '';
    const inactive = option?.isActive === false ? ' (inaktiv)' : '';
    return `${name}${inactive}${extra ? ` — ${extra}` : ''}`;
  }, []);

  const isOptionEqualToValue = useCallback((opt, val) => {
    return String(opt?.userId || '') === String(val?.userId || '');
  }, []);

  const selectedLabel = useMemo(() => {
    if (!selectedUser) return '';
    return optionLabel(selectedUser);
  }, [optionLabel, selectedUser]);

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: 2, display: 'flex', justifyContent: 'center' }}>
      <Paper sx={{ p: 3, width: '100%', maxWidth: 640 }}>
        <Typography variant='h5' sx={{ mb: 1 }}>
          Identität ändern
        </Typography>

        <Typography variant='body2' sx={{ mb: 2, opacity: 0.8 }}>
          Aktuell: {authCtx?.currentUser?.userName || 'Unbekannt'} ({authCtx?.currentUser?.userId || '—'})
        </Typography>

        <Alert severity='warning' sx={{ mb: 2 }}>
          Du loggst dich als ein anderer Nutzer ein. Nutze diese Funktion nur, wenn du dazu berechtigt bist.
        </Alert>

        <TextField
          label='Gerätetyp'
          fullWidth
          select
          SelectProps={{ native: true }}
          inputRef={deviceTypeRef}
          defaultValue='company'
          sx={{ mb: 2 }}
        >
          <option value='private'>Privat</option>
          <option value='company'>Firma</option>
          <option value='counter'>Kasse</option>
        </TextField>

        <Autocomplete
          options={options}
          value={selectedUser}
          onChange={(_, value) => setSelectedUser(value)}
          inputValue={inputValue}
          onInputChange={(_, value) => setInputValue(value)}
          isOptionEqualToValue={isOptionEqualToValue}
          getOptionLabel={optionLabel}
          filterOptions={x => x}
          loading={isLoading}
          renderInput={params => (
            <TextField
              {...params}
              label='Nutzer suchen'
              placeholder='Name, E-Mail, Username...'
              fullWidth
              sx={{ mb: 2 }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? <CircularProgress color='inherit' size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {selectedLabel ? (
          <Typography variant='body2' sx={{ mb: 2, opacity: 0.8 }}>
            Ausgewählt: {selectedLabel}
          </Typography>
        ) : null}

        <TextField
          inputRef={reasonRef}
          label='Grund für den Wechsel'
          placeholder='z. B. Fehler in der Kassenansicht nachstellen, den nur diese Kennung sieht'
          multiline
          minRows={2}
          fullWidth
          sx={{ mb: 1 }}
          helperText='Pflichtangabe. Der Grund steht im Audit-Log und geht per E-Mail an die Person, deren Kennung du übernimmst. Die Sitzung läuft nach einer Stunde ab.'
        />

        <Button variant='contained' onClick={onSwitch}>
          Als Nutzer einloggen
        </Button>
      </Paper>
    </Box>
  );
}

